import { clientApi } from "~/utils/clientApi";
import { DefaultAxiosRequestConfig } from "~/api/apiMethods";
import {
  DRAFT_PREVIEW,
  DRAFT_ORG_TYPE_V2,
  DRAFT_ACCOUNT_DETAILS_V2,
  DRAFT_COMPANY_DETAILS_V2,
  DRAFT_PAYFORM_TECH_DETAILS_V2,
  DRAFT_ADDITIONAL_DETAILS_V2,
  DRAFT_ATTACHMENTS_DETAILS_V2,
  PASSPORT_CHECK,
  OFFER_HTML,
  OFFER_CONFIRM_AGREEMENT,
  OFFER_UPLOAD,
  OFFER_REMOVE_FILE,
  OFFER_SET_URL,
  GENERATE_LOGO,
  DOMAIN_SUGGEST_BY_PRODUCT_DESCRIPTION,
  GENERATE_LOGO_LIST_BY_CLIENT_INFO,
  GENERATE_LOGO_BY_CLIENT_INFO,
  DRAFT_FEEDBACK,
  DRAFT_STEP_FEEDBACK,
  VALIDATE_WEBSITES,
  PROMOCODE_CHECK_AVAILABILITY,
  PRODAMUS_OFFER_TOGGLE_CHECKBOX,
} from "./apiUrls";
import {
  ClientId,
  AccountDetailsFormFieldsCustomerV2,
  CompanyDetailsFormFieldsCustomerV2,
  PayformTechDetailsFormFieldsCustomerV2,
  AdditionalDetailsFormFieldsCustomerV2,
  AttachmentsDetailsFormFieldsCustomerV2,
  NameStepsEnumCustomerV2,
  ValidateWebsitesParams,
  ValidatePromocodeParams,
  UIVersion
} from "~/typings/types";
import { cleanText, getSentryAnonId } from "~/utils";

export type ResSuccess = 1 | 0;

export interface ReqPostData<T> {
  id: ClientId;
  is_presave_mode?: boolean;
  fields: T;
  ui_version?: UIVersion;
}

export interface ReqPostArguments<T> {
  data: ReqPostData<T>;
  config?: DefaultAxiosRequestConfig;
}

export interface ErrorFieldFromBackend {
  code: "field_validation" | "confirmation:incorrect_code" | "code_confirmation:too_many_attempts";
  message: string;
  data: {
    target_type: string;
    target: string;
  };
}

export interface ResPost {
  success: ResSuccess;
  errors?: ErrorFieldFromBackend[];
}

export interface ApiResponseGetClientIdByHash {
  success: ResSuccess;
  data: {
    id: ClientId;
  };
}

interface DraftPrewievId {
  clientId: string;
}

export interface ApiResponseDraftPreviewId {
  success: 1 | 0;
  data: {
    questionnaire_id: ClientId;
    chapters: Partial<{
      [NameStepsEnumCustomerV2.AccountDetails]: AccountDetailsFormFieldsCustomerV2;
      [NameStepsEnumCustomerV2.AdditionalDetails]: AdditionalDetailsFormFieldsCustomerV2;
      [NameStepsEnumCustomerV2.CompanyDetails]: CompanyDetailsFormFieldsCustomerV2;
      [NameStepsEnumCustomerV2.PayformTechDetails]: PayformTechDetailsFormFieldsCustomerV2;
    }>;
    flat: FormData;
    ready_to_save: 1 | 0;
    errors: [];
    chapter_latest_int: number;
    chapter_latest: NameStepsEnumCustomerV2;
    shortcut_hash: string;
    should_generate_new_id?: boolean;
  };
}

export const getDraftPreviewId = async ({
  clientId
}: DraftPrewievId): Promise<ApiResponseDraftPreviewId> => {
  try {
    const res = await clientApi.get(`${DRAFT_PREVIEW}/${cleanText(clientId)}`);
    if (res?.data?.success === 0) {
      throw new Error("Error from backend, method: getDraftPreviewId");
    }
    return res.data;
  } catch (err: any) {
    throw new Error(err);
  }
};

type DraftEndpoints =
  | typeof DRAFT_ACCOUNT_DETAILS_V2
  | typeof DRAFT_COMPANY_DETAILS_V2
  | typeof DRAFT_PAYFORM_TECH_DETAILS_V2
  | typeof DRAFT_ADDITIONAL_DETAILS_V2
  | typeof DRAFT_ATTACHMENTS_DETAILS_V2

type DraftFormFields =
  | AccountDetailsFormFieldsCustomerV2
  | AdditionalDetailsFormFieldsCustomerV2
  | CompanyDetailsFormFieldsCustomerV2
  | PayformTechDetailsFormFieldsCustomerV2
  | AttachmentsDetailsFormFieldsCustomerV2

const postDraftDetails = async <T extends DraftFormFields>(
  endpoint: DraftEndpoints,
  { data, config }: ReqPostArguments<T>
): Promise<ResPost> => {
  try {
    const requestData = {
      ...data,
      is_presave_mode: data?.is_presave_mode || false
    };
    
    const res = await clientApi.post(endpoint, requestData, config);
    return res.data;
  } catch (err: any) {
    throw new Error(err);
  }
};

export const postDraftOrgType = async (
  clientId: string,
  orgType: string,
  uiVersion: UIVersion = "v1",
  ymClientId: string | null = null
): Promise<ResPost> => {
  const res = await clientApi.post(DRAFT_ORG_TYPE_V2, {
    id: cleanText(clientId),
    fields: { org_type: orgType },
    ui_version: uiVersion,
    ym_client_id: ymClientId,
    sentry_id: getSentryAnonId()
  });
  return res.data;
};

export const postDraftCompanyDetails = (args: ReqPostArguments<CompanyDetailsFormFieldsCustomerV2>) =>
  postDraftDetails(DRAFT_COMPANY_DETAILS_V2, args);

export const postDraftAccountDetails = (args: ReqPostArguments<AccountDetailsFormFieldsCustomerV2>) =>
  postDraftDetails(DRAFT_ACCOUNT_DETAILS_V2, args);

export const postDraftPayformTechDetails = (args: ReqPostArguments<PayformTechDetailsFormFieldsCustomerV2>) =>
  postDraftDetails(DRAFT_PAYFORM_TECH_DETAILS_V2, args);

export const postDraftAttachmentsDetails = (args: ReqPostArguments<AttachmentsDetailsFormFieldsCustomerV2>) =>
  postDraftDetails(DRAFT_ATTACHMENTS_DETAILS_V2, args);

export const postDraftAdditionalDetails = (args: ReqPostArguments<AdditionalDetailsFormFieldsCustomerV2>) =>
  postDraftDetails(DRAFT_ADDITIONAL_DETAILS_V2, args);

export interface BackendResponsePassportCheck {
  success: ResSuccess;
  data: string[];
}

export interface ReqPostArgumentsPassportCheck {
  file_id: number;
  page_type: string;
  questionnaire_uid: string;
  questionnaire_type: string;
}

export const postPassportCheck = async (args: ReqPostArgumentsPassportCheck) => await clientApi.post<BackendResponsePassportCheck>(PASSPORT_CHECK, args);

export const getOfferHtml = async (clientId: string) => await clientApi.get<BackendResponsePassportCheck>(OFFER_HTML, { params: { questionnaire_uid: clientId } });
export const getOfferConfirmAgreement = async (clientId: string) => await clientApi.get<BackendResponsePassportCheck>(OFFER_CONFIRM_AGREEMENT, { params: { questionnaire_uid: clientId } });
export const postOfferUpload = async (data: FormData, config: DefaultAxiosRequestConfig, clientId: string) => await clientApi.post(`${OFFER_UPLOAD}?questionnaire_uid=${clientId}`, data, config);
export const removeOfferFile = async (clientId: string) => await clientApi.get(`${OFFER_REMOVE_FILE}?questionnaire_uid=${clientId}`);
export const setOfferUrl = async (clientId: string, url: string) => await clientApi.get(`${OFFER_SET_URL}?questionnaire_uid=${clientId}&url=${url}`);

export const createGeneratedLogo = async (clientId: string, productType: string, productName: string, productDescription: string) => await clientApi.post(`${GENERATE_LOGO_BY_CLIENT_INFO}`, { id: clientId, product_type: productType, product_name: productName, product_description: productDescription });
export const getGeneratedLogo = async (clientId: string) => await clientApi.get(`${GENERATE_LOGO_LIST_BY_CLIENT_INFO}?id=${clientId}`);
export const getGeneratedDomains = async (payload: any) => await clientApi.post(`${DOMAIN_SUGGEST_BY_PRODUCT_DESCRIPTION}`, payload);

export interface FeedbackData {
  questionnaire_type: string;
  questionnaire_id: number | string;
  rating?: number;
  description?: string;
  ym_client_id?: string;
  sentry_id?: string;
  reason?: string;
}

interface PostDraftFeedbackParams {
  body: FeedbackData;
  signal: AbortSignal;
}
export const postDraftFeedback = async (args: PostDraftFeedbackParams) => await clientApi.post(`${DRAFT_FEEDBACK}`, args.body, { signal: args.signal })

export interface StepFeedbackPayload {
  draft_uuid: string;
  draft_type: string;
  step: string;
  description_option?: string | null;
  description?: string | null;
  ym_client_id?: string | null;
  sentry_id?: string | null;
}

export const postDraftStepFeedback = async (payload: StepFeedbackPayload): Promise<{ id: number; created_at: string } | null> => {
  const res = await clientApi.post(DRAFT_STEP_FEEDBACK, payload);
  return res?.data?.data || null;
};

export type PostValidateWebsitesParams = {
  body: ValidateWebsitesParams;
};

export const postValidateWebsites = async (params: PostValidateWebsitesParams) => await clientApi.post(VALIDATE_WEBSITES, params.body)
export const postValidatePromocode = async (params: ValidatePromocodeParams) => await clientApi.post(PROMOCODE_CHECK_AVAILABILITY, params)

export interface OfferCheckboxItem {
  questionnaire_uid: string;
  offer_url: string;
  action: "check" | "uncheck";
  website_address?: string;
  checkbox_text?: string;
}

export const postToggleOfferCheckbox = async (checkboxes: OfferCheckboxItem[]) =>
  await clientApi.post(PRODAMUS_OFFER_TOGGLE_CHECKBOX, { checkboxes });